import { showError } from 'src/components/alertNotification';
import { IClientDetailDto, IUserInfoClientDto } from 'src/graphql/auto-generated/graphql';
import { DeleteStorageComponent, GetStorageComponent, SetStorageComponent } from 'src/utils/session';

export type IExecutiveClient = Record<string, string>;
interface AuthTokenService {
  setAccessToken: (token: string) => void;
  getAccessToken: () => string;
  setRefreshToken: (token: string) => void;
  getRefreshToken: () => string;
  clearToken: () => void;
  setUserDetails: (obj: any) => void;
  getUserDetails(): IUserInfoClientDto | null;
  getClientDetails(): IClientDetailDto | IExecutiveClient | null;
  setClientDetails(props: { clientInfo?: IClientDetailDto; clientIdsEncoded?: string }): void;
}

const encodeToken = (token: string) => {
  try {
    const tokenWithBrowserData = JSON.stringify({ tkvrt: token });
    const tokenWithBrowserDataEncoded = Buffer.from(tokenWithBrowserData, 'utf-8').toString('base64');
    const tokenWithBrowserDataEncodedSplit = [
      tokenWithBrowserDataEncoded.substring(0, 20),
      tokenWithBrowserDataEncoded.substring(20),
    ]
      .reverse()
      .join('');
    return tokenWithBrowserDataEncodedSplit;
  } catch (e) {
    console.log('Error encoding token', e);
    return token;
  }
};

const decodeToken = (token: string) => {
  if (!token) return '';

  try {
    const tokenWithBrowserDataEncodedSplit = [token.substring(0, token.length - 20), token.substring(token.length - 20)]
      .reverse()
      .join('');

    const tokenWithBrowserData = Buffer.from(tokenWithBrowserDataEncodedSplit, 'base64').toString('utf-8');
    const { tkvrt } = JSON.parse(tokenWithBrowserData);
    return tkvrt;
  } catch (error) {
    console.log('Error decoding token', error);
    clearToken();
    showError('Your session has expired');
    return token;
  }
};

function clearToken() {
  DeleteStorageComponent('clientAccessToken');
  DeleteStorageComponent('clientRefreshToken');
  DeleteStorageComponent('userDetails');
  DeleteStorageComponent('clientDetails');
  DeleteStorageComponent('executiveClients');
}

function setAccessToken(token: string) {
  try {
    SetStorageComponent('clientAccessToken', encodeToken(token));
  } catch (e) {
    console.log('Error setting token:', e);
  }
}

function getAccessToken(): string {
  let client_access_token = '';
  try {
    client_access_token = decodeToken(GetStorageComponent('clientAccessToken') || '');
  } catch (error) {
    console.log('Error getting access token', error);
  }
  return client_access_token;
}

function setRefreshToken(token: string) {
  try {
    SetStorageComponent('clientRefreshToken', encodeToken(token));
  } catch (e) {
    console.log('Error setting token:', e);
  }
}

function getRefreshToken(): string {
  let client_refresh_token = '';
  try {
    client_refresh_token = decodeToken(GetStorageComponent('clientRefreshToken') || '');
  } catch (error) {
    console.log('Error getting refresh token', error);
  }
  return client_refresh_token;
}

function getUserDetails(): IUserInfoClientDto | null {
  try {
    const token = GetStorageComponent('userDetails');
    if (!token) return null;

    return decodeToken(token) as IUserInfoClientDto;
  } catch (e) {
    console.log('Local store error', e);
    return null;
  }
}

function setUserDetails(obj: any) {
  if (!obj) return null;
  try {
    SetStorageComponent('userDetails', encodeToken(obj as string));
  } catch (e) {
    console.log('Local store error', e);
  }
}

function getClientDetails(): IClientDetailDto | null {
  try {
    const clientDetails = GetStorageComponent('clientDetails');
    const executiveClients = GetStorageComponent('executiveClients');
    //For executives
    if (executiveClients) {
      const decoded = Buffer.from(executiveClients, 'base64').toString('utf-8');
      return JSON.parse(decoded) as IExecutiveClient;
    }

    //For client
    else if (clientDetails) {
      return decodeToken(clientDetails) as IClientDetailDto;
    }

    return null;
  } catch (e) {
    console.log('Local store error', e);
    return null;
  }
}

function setClientDetails(props: { clientInfo?: IClientDetailDto; clientIdsEncoded?: string }) {
  const { clientInfo, clientIdsEncoded } = props;
  try {
    if (clientIdsEncoded) {
      SetStorageComponent('executiveClients', clientIdsEncoded);
    } else {
      SetStorageComponent('clientDetails', encodeToken(clientInfo as string));
    }
  } catch (e) {
    console.log('Local store error', e);
  }
}

const TokenService: AuthTokenService = {
  setAccessToken,
  getAccessToken,
  setRefreshToken,
  getRefreshToken,
  clearToken,
  setClientDetails,
  getClientDetails,
  setUserDetails,
  getUserDetails,
};

export default TokenService;
